import { assign } from '@atds-legacy/lang';
import EventEmitter from '@atds-legacy/events';
import Hammer from 'hammerjs';
import { DeviceInfo } from '@/shared/libs';

delete Hammer.defaults.cssProps.userSelect;

export default function Slider(frame, opts = {}) {
  const wrapper = frame.querySelector('.js-slider-wrapper');
  const bus = new EventEmitter();
  const tileSpacing = parseInt(getComputedStyle(wrapper).getPropertyValue('--tile-spacing'), 10);
  const acceptableDistance = tileSpacing / 2 + 1;

  opts = assign({
    step: 'window',
    duration: 500,
    index: 0,
    touch: DeviceInfo.isTouchDevice(),
  }, opts);

  let slideWidth;
  let slidesWidth;
  let frameWidth;
  let slides;

  let index = opts.index || 0;
  let position = 0;
  let itemsPerFrame = 0;
  let spareSpace = 0;

  const hammer = new Hammer.Manager(frame);
  hammer.add(new Hammer.Pan({ direction: Hammer.DIRECTION_HORIZONTAL, threshold: 42 }));

  let panXStart = 0;

  function init() {
    calculateDims();
    refreshClasses();

    if (opts.touch) {
      hammer.on('panstart', onPanStart);
      hammer.on('panleft panright', onPanHorizontal);
      hammer.on('panend', onPanEnd);
    }
  }

  function update(additionalSpace = 0) {
    spareSpace = additionalSpace;
    calculateDims();
    slideTo(index, 0);
    refreshClasses();
  }

  function getSlideWidth() {
    const item = wrapper.querySelector('.js-slider-item');

    if (item) {
      return item.getBoundingClientRect().width || item.offsetWidth;
    }

    return slideWidth;
  }

  function calculateDims() {
    slides = Array.prototype.slice.call(wrapper.children);
    slidesWidth = slides.reduce((previousValue, slide) => {
      return previousValue + slide.getBoundingClientRect().width || slide.offsetWidth;
    }, 0);

    if (slides.length) {
      const item = slides[0];
      frameWidth = frame.getBoundingClientRect().width || frame.offsetWidth;
      const itemWidth = item.getBoundingClientRect().width || item.offsetWidth;
      slideWidth = opts.step === 'window' ? frameWidth : itemWidth;
    }

    itemsPerFrame = Math.floor(frameWidth / getSlideWidth()) - spareSpace;

    if (slideWidth > slidesWidth) {
      slidesWidth = slideWidth;
    }
  }

  function next(_duration) {
    calculateDims();

    index += itemsPerFrame;
    slideTo(index, _duration || opts.duration);
    bus.emit('next');
  }

  function prev(_duration) {
    calculateDims();

    index -= itemsPerFrame;
    slideTo(index, _duration || opts.duration);
    bus.emit('prev');
  }

  function slideTo(_index, _duration) {
    const duration = _duration >= 0 ? _duration : opts.duration;
    index = _index;

    if (index > slides.length - itemsPerFrame) {
      index = slides.length - itemsPerFrame;
    }

    if (index < 0) {
      index = 0;
    }

    position = index * getSlideWidth();
    translate(position, duration);
    bus.emit('change');
  }

  function onPanStart(event) {
    panXStart = event.center.x;
  }

  function onPanHorizontal(event) {
    translate(position + panXStart - event.center.x, 0);
  }

  function onPanEnd(event) {
    if (panXStart > event.center.x && hasNext()) {
      next(300);
    } else if (panXStart < event.center.x && hasPrev()) {
      prev(300);
    } else {
      slideTo(index, 300);
    }
  }

  function hasNext() {
    const item = wrapper.firstChild;
    if (!(item)) {
      return false;
    }

    return index + itemsPerFrame < slides.length;
  }

  function hasPrev() {
    return index > 0;
  }

  function translate(position, duration) {
    if (duration > 0) {
      frame.style['transition-timing-function: ease'] = 'ease';

      const onTransitionEnd = (e) => {
        if (e.propertyName !== 'transform') {
          return;
        }

        wrapper.removeEventListener('transitionend', onTransitionEnd);
        refreshClasses();
      };

      wrapper.addEventListener('transitionend', onTransitionEnd);
    }

    wrapper.style.transition = `transform ${duration}ms`;
    wrapper.style.transform = `translate3d(${-position}px, 0px, 0px)`;
  }

  function refreshClasses() {
    const parentRect = wrapper.parentElement.getBoundingClientRect();

    slides.forEach((slide) => {
      const slideRect = slide.getBoundingClientRect();

      slide.classList.remove('is-first-visible', 'is-last-visible', 'is-visible');

      if (Math.abs(parentRect.left - slideRect.left) < acceptableDistance) {
        slide.classList.add('is-first-visible');
      }

      if (Math.abs(parentRect.right - slideRect.right) < slideRect.width) {
        slide.classList.add('is-last-visible');
      }

      if (slideRect.left >= parentRect.left - acceptableDistance && slideRect.left <= parentRect.right + acceptableDistance) {
        slide.classList.add('is-visible');
      }
    });
  }

  function reset() {
    calculateDims();
    slideTo(0, 0);
    refreshClasses();
  }

  function getIndex() {
    return index;
  }

  function destroy() {
    hammer.off('panstart', onPanStart);
    hammer.off('panleft panright', onPanHorizontal);
    hammer.off('panend', onPanEnd);
    hammer.destroy();
  }

  init();

  return {
    reset,
    destroy,
    next,
    prev,
    slideTo,
    getIndex,
    hasNext,
    hasPrev,
    update,
    getSlideSize: () => itemsPerFrame,
    on: (type, listener) => bus.on(type, listener),
    off: (type, listener) => bus.off(type, listener),
  };
}
